import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  TextField,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import type { RankedMatchResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { MatchList } from "../components/MatchList";
import { useMatchHistory } from "../hooks/useMatchHistory";
import { FacilityScopePicker } from "../../../common/components/FacilityScopePicker";

export function MatchHistoryPage() {
  const { user } = useAuth();
  const myUid = user?.uid ?? null;
  const [params, setParams] = useSearchParams();
  const sourceValue = params.get("source") ?? "";
  const source = ["queue", "league", "tournament"].includes(sourceValue)
    ? sourceValue
    : "";
  const statusValue = params.get("status") ?? "";
  const status = ["pending", "completed", "disputed", "invalid"].includes(
    statusValue,
  )
    ? statusValue
    : "";
  const requestedOffset = Number(params.get("offset") || 0);
  const offset =
    Number.isSafeInteger(requestedOffset) && requestedOffset >= 0
      ? requestedOffset
      : 0;
  function setFilter(key: "source" | "status", value: string) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete("offset");
      return next;
    });
  }
  function setOffset(value: number) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set("offset", String(value));
      else next.delete("offset");
      return next;
    });
  }
  const historyQ = useMatchHistory({
    facility_id: params.get("facility_id") || undefined,
    source: source || undefined,
    status: status || undefined,
    limit: 25,
    offset,
  });
  const matches: RankedMatchResponse[] = historyQ.data ?? [];
  const ordered = matches;

  if (historyQ.isLoading) {
    return (
      <Page title="Match history">
        <Stack
          data-testid="match-history-skeleton"
          role="status"
          aria-busy="true"
          spacing={1}
        >
          <Typography color="text.secondary">Loading your matches…</Typography>
          <Skeleton variant="rounded" height={72} />
        </Stack>
      </Page>
    );
  }
  if (historyQ.isError) {
    return (
      <Page title="Match history">
        <Alert severity="error" role="alert">
          Couldn&apos;t load your match history. Please try again.
        </Alert>
      </Page>
    );
  }
  return (
    <Page title="Match history">
      <Stack spacing={2}>
        <FacilityScopePicker
          facilityId={params.get("facility_id") || undefined}
          onChange={(nextId) => {
            setParams((previous) => {
              const next = new URLSearchParams(previous);
              if (nextId) next.set("facility_id", nextId);
              else next.delete("facility_id");
              next.delete("offset");
              return next;
            });
          }}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            select
            label="Source"
            value={source}
            onChange={(event) => {
              setFilter("source", event.target.value);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All sources</MenuItem>
            {["queue", "league", "tournament"].map((value) => (
              <MenuItem key={value} value={value}>
                {value === "queue" ? "Drop-in" : value}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(event) => {
              setFilter("status", event.target.value);
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {["pending", "completed", "disputed", "invalid"].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {!ordered.length ? <Typography>No matches found.</Typography> : null}
        {ordered.length ? (
          <Paper variant="outlined">
            <MatchList matches={ordered} viewerId={myUid} />
          </Paper>
        ) : null}
        <Stack direction="row">
          <Button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 25))}
          >
            Previous
          </Button>
          <Button
            disabled={matches.length < 25}
            onClick={() => setOffset(offset + 25)}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Page>
  );
}
